import { Shield, Star, BadgeCheck, Clock, Phone, ArrowRight, Sparkles, Flame } from "lucide-react";

const STATS = [
  { icon: BadgeCheck, big: "500+", label: "Yards Served" },
  { icon: Star, big: "5.0", label: "Avg Rating" },
  { icon: Shield, big: "100%", label: "Veteran Owned" },
  { icon: Clock, big: "48 hr", label: "Quote Turnaround" },
];

const TRUST = [
  "Licensed & Insured",
  "Same-Crew Every Visit",
  "Satisfaction Guarantee",
  "No Long-Term Contracts",
  "Free On-Site Estimate",
];

export default function ConversionBanner() {
  return (
    <section
      id="why-ironclad"
      data-testid="conversion-banner"
      className="relative bg-black border-y border-border overflow-hidden"
    >
      {/* Background sheen */}
      <div className="absolute -top-32 -right-32 w-[600px] h-[600px] bg-primary/8 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 left-1/4 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container-ic relative py-16 md:py-24">
        {/* Top hype row */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12 md:mb-16">
          <div className="max-w-3xl">
            <div className="overline mb-4" data-testid="conv-overline">
              <Flame className="inline h-3.5 w-3.5 mr-2 -mt-0.5" />
              Why San Antonio Books Ironclad
            </div>
            <h2
              className="font-display text-white text-4xl md:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.95]"
              data-testid="conv-headline"
            >
              Sharper yards. <br />
              <span className="text-primary">Zero excuses.</span>
            </h2>
            <p className="mt-5 text-foreground/75 text-base md:text-lg max-w-xl">
              We don&apos;t just mow — we restore curb appeal, raise property value, and
              free up your weekend. Built by a Navy vet. Run like a mission.
            </p>
          </div>
          <div className="hidden md:flex flex-col items-end">
            <div className="flex items-center gap-1 mb-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-primary text-primary" />
              ))}
            </div>
            <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
              500+ San Antonio Homes Served
            </div>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border border border-border mb-14">
          {STATS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={i}
                className="bg-black p-6 md:p-8 group hover:bg-primary/5 transition-colors"
                data-testid={`conv-stat-${i}`}
              >
                <Icon className="h-5 w-5 text-primary mb-3 transition-transform group-hover:scale-110" />
                <div className="font-display text-4xl md:text-5xl font-black text-white tracking-tighter leading-none">
                  {s.big}
                </div>
                <div className="mt-2 text-[10px] md:text-xs uppercase tracking-[0.25em] text-muted-foreground">
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Offer card + Trust column */}
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
          {/* Offer card */}
          <div
            className="lg:col-span-7 relative bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/50 p-7 md:p-10 overflow-hidden"
            data-testid="conv-offer-card"
          >
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-primary/20 rounded-full blur-2xl" />
            <div className="absolute top-5 right-5 bg-primary text-primary-foreground px-3 py-1 text-[10px] uppercase tracking-[0.3em] font-black animate-pulse">
              <Sparkles className="inline h-3 w-3 mr-1 -mt-0.5" />
              Limited
            </div>
            <div className="overline mb-3 text-primary">Halloween Bonus Stack</div>
            <h3 className="font-display text-white text-3xl md:text-4xl lg:text-5xl font-black uppercase tracking-tighter leading-[0.95]">
              Book now. <br />
              Look <span className="text-primary">Ironclad</span> by October 31.
            </h3>

            <ul className="mt-7 space-y-3.5">
              <li className="flex items-start gap-3 text-foreground/95">
                <span className="font-display text-primary text-2xl font-black leading-none">
                  +
                </span>
                <div>
                  <span className="font-bold text-white">HALLOWEEN SPECIAL</span>{" "}
                  for San Antonio homeowners — book by October 31
                </div>
              </li>
              <li className="flex items-start gap-3 text-foreground/95">
                <span className="font-display text-primary text-2xl font-black leading-none">
                  +
                </span>
                <div>
                  <span className="font-bold text-white">FREE</span> edge &amp;
                  trim with every mulch service
                </div>
              </li>
              <li className="flex items-start gap-3 text-foreground/95">
                <span className="font-display text-primary text-2xl font-black leading-none">
                  +
                </span>
                <div>
                  <span className="font-bold text-white">10% VETERAN</span>{" "}
                  discount — always honored
                </div>
              </li>
            </ul>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href="#booking"
                className="btn-primary group"
                data-testid="conv-cta-book"
              >
                Lock In My Spot
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="tel:5129480676"
                className="btn-outline"
                data-testid="conv-cta-call"
              >
                <Phone className="h-4 w-4" /> 512-948-0676
              </a>
            </div>
            <div className="mt-5 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
              Free estimates · No long-term contract · Spots fill weekly
            </div>
          </div>

          {/* Trust column */}
          <div className="lg:col-span-5 flex flex-col justify-between">
            <div>
              <div className="overline mb-4">The Ironclad Promise</div>
              <ul className="space-y-3">
                {TRUST.map((t) => (
                  <li
                    key={t}
                    className="flex items-center gap-3 border-b border-border pb-3 last:border-b-0"
                    data-testid={`conv-trust-${t.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <div className="w-7 h-7 border border-primary/50 bg-primary/5 flex items-center justify-center shrink-0">
                      <BadgeCheck className="h-4 w-4 text-primary" />
                    </div>
                    <span className="text-sm md:text-base font-display uppercase tracking-tight text-white font-bold">
                      {t}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Testimonial pull */}
            <div className="mt-6 lg:mt-8 border-l-2 border-primary pl-5 py-1">
              <p className="font-display italic text-white/90 text-base md:text-lg leading-snug">
                &ldquo;Sharpest <span className="text-primary">lawn</span> on the block.
                Discipline, consistency, curb appeal.&rdquo;
              </p>
              <div className="mt-2 text-[10px] uppercase tracking-[0.3em] text-primary">
                Aaron P. · New Braunfels, TX
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
